import React, { FC, useEffect, useState } from 'react';
import {
  GlobalDesignGroupAction,
  GlobalDesignPanelProps,
  GlobalDesignStyleGroup,
} from './types';
import { EditorPointer } from '@wix/editor-document-logic-api';
import { CompRef } from '@wix/document-services-types';
import { Box, TextButton } from '@wix/design-system';
import { ChevronLeft } from '@wix/wix-ui-icons-common';
import LobbyHeader from './common/lobby/LobbyHeader';
import StyleLobbyList from './styleLobbyList';
import { Host } from '@wix/sdk';
import { useTranslation } from '../useTranslation';
import { useBI } from './hooks/useBI';

const getGroupedItems = async (
  editorPointers: EditorPointer<CompRef>[],
  action: GlobalDesignGroupAction,
  getHost: GlobalDesignPanelProps['getHost'],
  componentsAPI: GlobalDesignPanelProps['componentsAPI'],
): Promise<GlobalDesignStyleGroup[]> => {
  const groupingStrategy = action.grouping.getGroupingStrategy(
    editorPointers,
    getHost,
    componentsAPI,
  );

  return groupingStrategy();
};

export const GroupsActionPanel: FC<
  GlobalDesignPanelProps & {
    action: GlobalDesignGroupAction;
  }
> = ({
  action,
  editorPointers,
  getHost,
  actionName,
  componentsAPI,
  stylePresets,
}) => {
  const [t] = useTranslation();
  const { trackBackButtonClick, trackLobbyItemClick } = useBI();

  const [groupedItems, setGroupedItems] = useState<GlobalDesignStyleGroup[]>(
    [],
  );
  const [selectedItem, setSelectedItem] =
    useState<GlobalDesignStyleGroup | null>(null);
  const [selectedItemHost, setSelectedItemHost] = useState<Host | null>(null);

  const showSelectedItem = selectedItem && selectedItemHost;
  const isSingleGroupedItem =
    groupedItems.length === 1 ||
    (groupedItems.length === 0 && stylePresets?.length);

  useEffect(() => {
    (async () => {
      if (!selectedItem) {
        const _groupedItems = await getGroupedItems(
          editorPointers,
          action,
          getHost,
          componentsAPI,
        );

        if (
          _groupedItems.length === 1 ||
          (_groupedItems.length === 0 && stylePresets?.length)
        ) {
          setSelectedItem(_groupedItems[0]);
        }

        setGroupedItems(_groupedItems);
      }
    })();
  }, [action, editorPointers, getHost, componentsAPI, selectedItem]);

  useEffect(() => {
    (async () => {
      const host = selectedItem ? await selectedItem.createHost() : null;

      setSelectedItemHost(host);
    })();
  }, [selectedItem]);

  const onBackButtonClick = () => {
    trackBackButtonClick(actionName);
    resetSelection();
  };

  const onLobbyItemSelect = (item: GlobalDesignStyleGroup) => {
    trackLobbyItemClick(actionName);
    setSelectedItem(item);
  };

  const resetSelection = () => {
    setSelectedItem(null);
    setSelectedItemHost(null);
  };

  const handlePresetApplied = async () => {
    if (groupedItems.length === 0) {
      // Re-fetch grouped items after preset is applied
      const _groupedItems = await getGroupedItems(
        editorPointers,
        action,
        getHost,
        componentsAPI,
      );
      setGroupedItems(_groupedItems);

      // Only update selectedItem if we're in lobby view (not currently viewing a panel)
      // This prevents unnecessary re-renders and state loss in the preset components
      if (!selectedItem) {
        if (
          _groupedItems.length === 1 ||
          (_groupedItems.length === 0 && stylePresets?.length)
        ) {
          setSelectedItem(_groupedItems[0]);
        }
      }
      // If selectedItem exists, keep it stable to avoid re-renders
    }
  };

  if (!isSingleGroupedItem && (groupedItems.length === 0 || !action)) {
    return null;
  }
  return (
    <Box direction="vertical" width="100%">
      {showSelectedItem ? (
        <>
          {!isSingleGroupedItem && (
            <Box direction="vertical" width="fit-content" marginBottom="16px">
              <TextButton
                prefixIcon={<ChevronLeft />}
                size="small"
                skin="dark"
                onClick={onBackButtonClick}
              >
                {t('globalDesign.text.backHeader')}
              </TextButton>
            </Box>
          )}
          {React.createElement(action.panel, {
            host: selectedItemHost,
            componentsAPI,
            stylePresets,
            onPresetApplied: handlePresetApplied,
            group: selectedItem,
          })}
        </>
      ) : (
        <Box direction="vertical" width="100%">
          <LobbyHeader
            actionName={actionName}
            stylesCount={groupedItems.length}
          />
          <StyleLobbyList
            actionName={actionName}
            items={groupedItems}
            action={action}
            onItemSelect={onLobbyItemSelect}
            componentsAPI={componentsAPI}
          />
        </Box>
      )}
    </Box>
  );
};
